import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import { DateRange, DateRangePicker } from '@mui/x-date-pickers-pro';
import {
  CompanyShareSelection,
  IChildCompany,
  IChildStation,
} from '../../../dto/MessageSharing.dto';
import { ICompanySchool } from '../../../dto/CompanySchool.dto';
import { useRqListCmpySchoolsFacilities } from '../../../react_query/company_schools/ListSchools-Facilities';
import {
  useRqChildCompanies,
  useRqChildStations,
} from '../../../react_query/messages/sharing';
import {
  getDefaultShareWindowDraft,
  toDateTimeLocalValue,
} from './buildShareTargets';

const getShareRangePickerValue = (
  selection: Pick<CompanyShareSelection, 'startsAt' | 'endsAt'>,
): DateRange<dayjs.Dayjs | null> => {
  return [
    selection.startsAt ? dayjs(selection.startsAt) : null,
    selection.endsAt ? dayjs(selection.endsAt) : null,
  ];
};

interface MessageVisibilityEditorProps {
  companyId: number;
  value: CompanyShareSelection[];
  onChange: (value: CompanyShareSelection[]) => void;
  allowSchoolTargets?: boolean;
  disabled?: boolean;
  title?: string;
  helperText?: string;
}

const MessageVisibilityEditor: React.FC<MessageVisibilityEditorProps> = ({
  companyId,
  value,
  onChange,
  allowSchoolTargets = true,
  disabled = false,
  title = 'District Sharing',
  helperText = 'Choose child companies, then either share to the whole company or target specific stations and schools with an active window.',
}) => {
  const {data: childCompanies, isLoading} = useRqChildCompanies(
    companyId,
    Boolean(companyId),
  );

  useEffect(() => {
    if (!childCompanies?.length) {
      return;
    }

    let hasChanges = false;
    const nextValue = value.map(selection => {
      const companyName =
        childCompanies.find(company => company.id === selection.companyId)
          ?.name ?? selection.companyName;

      if (companyName === selection.companyName) {
        return selection;
      }

      hasChanges = true;

      return {
        ...selection,
        companyName,
      };
    });

    if (hasChanges) {
      onChange(nextValue);
    }
  }, [childCompanies, onChange, value]);

  const activeChildCompanies = useMemo(
    () => (childCompanies || []).filter(company => company.is_active),
    [childCompanies],
  );

  const selectedCompanyIds = useMemo(
    () => new Set(value.map(selection => selection.companyId)),
    [value],
  );

  const handleCompanyToggle = useCallback(
    (company: IChildCompany) => {
      if (selectedCompanyIds.has(company.id)) {
        onChange(value.filter(selection => selection.companyId !== company.id));

        return;
      }

      onChange([
        ...value,
        {
          companyId: company.id,
          companyName: company.name,
          wholeCompany: true,
          selectedStationIds: [],
          selectedSchoolIds: [],
          ...getDefaultShareWindowDraft(),
        },
      ]);
    },
    [onChange, selectedCompanyIds, value],
  );

  const handleSelectionChange = useCallback(
    (
      companyIdToUpdate: number,
      updater: (selection: CompanyShareSelection) => CompanyShareSelection,
    ) => {
      onChange(
        value.map(selection =>
          selection.companyId === companyIdToUpdate
            ? updater(selection)
            : selection,
        ),
      );
    },
    [onChange, value],
  );

  const handleRemoveSelection = useCallback(
    (companyIdToRemove: number) => {
      onChange(
        value.filter(selection => selection.companyId !== companyIdToRemove),
      );
    },
    [onChange, value],
  );

  return (
    <Card>
      <CardHeader title={title} />
      <CardContent>
        <Stack spacing={2.5}>
          <Alert severity='info'>{helperText}</Alert>

          <Box>
            <Typography variant='subtitle2' sx={{mb: 1}}>
              Select Child Companies
            </Typography>
            {isLoading ? (
              <Box>
                <Skeleton height={42} />
                <Skeleton height={42} />
                <Skeleton height={42} />
              </Box>
            ) : activeChildCompanies.length > 0 ? (
              <List
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  maxHeight: 240,
                  overflow: 'auto',
                }}>
                {activeChildCompanies.map(company => {
                  const isSelected = selectedCompanyIds.has(company.id);

                  return (
                    <ListItemButton
                      key={company.id}
                      selected={isSelected}
                      disabled={disabled}
                      onClick={() => handleCompanyToggle(company)}
                      divider>
                      <Checkbox
                        checked={isSelected}
                        disableRipple
                        tabIndex={-1}
                      />
                      <ListItemText primary={company.name} />
                    </ListItemButton>
                  );
                })}
              </List>
            ) : (
              <Typography color='text.secondary'>
                No active child companies are available for sharing.
              </Typography>
            )}
          </Box>

          {value.length === 0 ? (
            <Typography color='text.secondary'>
              Select at least one child company to configure district sharing.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {value.map(selection => (
                <CompanySelectionCard
                  key={selection.companyId}
                  selection={selection}
                  disabled={disabled}
                  allowSchoolTargets={allowSchoolTargets}
                  onChange={updater =>
                    handleSelectionChange(selection.companyId, updater)
                  }
                  onRemove={() => handleRemoveSelection(selection.companyId)}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default MessageVisibilityEditor;

const CompanySelectionCard = ({
  selection,
  disabled,
  allowSchoolTargets,
  onChange,
  onRemove,
}: {
  selection: CompanyShareSelection;
  disabled: boolean;
  allowSchoolTargets: boolean;
  onChange: (
    updater: (selection: CompanyShareSelection) => CompanyShareSelection,
  ) => void;
  onRemove: () => void;
}) => {
  const handleDateRangeChange = useCallback(
    (value: DateRange<dayjs.Dayjs | null>) => {
      onChange(currentSelection => ({
        ...currentSelection,
        startsAt: value[0] ? toDateTimeLocalValue(value[0]) : '',
        endsAt: value[1] ? toDateTimeLocalValue(value[1]) : '',
      }));
    },
    [onChange],
  );

  const handleWholeCompanyToggle = useCallback(
    (checked: boolean) => {
      onChange(currentSelection => ({
        ...currentSelection,
        wholeCompany: checked,
        selectedStationIds: checked ? [] : currentSelection.selectedStationIds,
        selectedSchoolIds: checked ? [] : currentSelection.selectedSchoolIds,
      }));
    },
    [onChange],
  );

  const handleStationToggle = useCallback(
    (stationId: number) => {
      onChange(currentSelection => {
        const hasStation =
          currentSelection.selectedStationIds.includes(stationId);

        return {
          ...currentSelection,
          selectedStationIds: hasStation
            ? currentSelection.selectedStationIds.filter(id => id !== stationId)
            : [...currentSelection.selectedStationIds, stationId],
        };
      });
    },
    [onChange],
  );

  const handleSchoolToggle = useCallback(
    (schoolId: number) => {
      onChange(currentSelection => {
        const hasSchool = currentSelection.selectedSchoolIds.includes(schoolId);

        return {
          ...currentSelection,
          selectedSchoolIds: hasSchool
            ? currentSelection.selectedSchoolIds.filter(id => id !== schoolId)
            : [...currentSelection.selectedSchoolIds, schoolId],
        };
      });
    },
    [onChange],
  );

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
      }}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        spacing={2}>
        <Box>
          <Typography variant='subtitle1' fontWeight={600}>
            {selection.companyName || `Company #${selection.companyId}`}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Configure the active window and target scope for this company.
          </Typography>
        </Box>
        <IconButton
          aria-label='Remove company share selection'
          onClick={onRemove}
          disabled={disabled}>
          <Clear />
        </IconButton>
      </Stack>

      <Grid container spacing={2} sx={{mt: 0.5, mb: 1}}>
        <Grid item xs={12} md={8}>
          <DateRangePicker
            label='Active Dates'
            value={getShareRangePickerValue(selection)}
            onChange={handleDateRangeChange}
            disabled={disabled}
            localeText={{start: 'Starts At', end: 'Ends At'}}
            sx={{width: '100%'}}
            slotProps={{
              textField: {
                size: 'small',
                sx: {width: '100%'},
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selection.wholeCompany}
                onChange={event =>
                  handleWholeCompanyToggle(event.target.checked)
                }
                disabled={disabled}
              />
            }
            label='Share to whole company'
          />
        </Grid>
      </Grid>

      {selection.wholeCompany ? (
        <Typography variant='body2' color='text.secondary'>
          Every station and school in this child company will receive the shared
          message during the selected window.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={allowSchoolTargets ? 6 : 12}>
            <Typography variant='subtitle2' sx={{mb: 1}}>
              Stations
            </Typography>
            <StationChecklist
              companyId={selection.companyId}
              selectedStationIds={selection.selectedStationIds}
              onToggle={handleStationToggle}
              disabled={disabled}
            />
          </Grid>
          {allowSchoolTargets ? (
            <Grid item xs={12} md={6}>
              <Typography variant='subtitle2' sx={{mb: 1}}>
                Schools
              </Typography>
              <SchoolChecklist
                companyId={selection.companyId}
                selectedSchoolIds={selection.selectedSchoolIds}
                onToggle={handleSchoolToggle}
                disabled={disabled}
              />
            </Grid>
          ) : null}
        </Grid>
      )}
    </Box>
  );
};

const StationChecklist = ({
  companyId,
  selectedStationIds,
  onToggle,
  disabled,
}: {
  companyId: number;
  selectedStationIds: number[];
  onToggle: (stationId: number) => void;
  disabled: boolean;
}) => {
  const {data: stations, isLoading} = useRqChildStations(companyId, true);

  const eligibleStations = useMemo(
    () =>
      (stations || []).filter(
        (station: IChildStation) => !station.is_district_owned,
      ),
    [stations],
  );

  if (isLoading) {
    return (
      <Box>
        <Skeleton height={36} />
        <Skeleton height={36} />
        <Skeleton height={36} />
      </Box>
    );
  }

  if (!eligibleStations.length) {
    return (
      <Typography variant='body2' color='text.secondary'>
        No eligible stations found for this company.
      </Typography>
    );
  }

  return (
    <List
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        maxHeight: 240,
        overflow: 'auto',
      }}>
      {eligibleStations.map(station => (
        <ListItemButton
          key={station.id}
          onClick={() => onToggle(station.id)}
          disabled={disabled}
          divider>
          <Checkbox
            checked={selectedStationIds.includes(station.id)}
            disableRipple
            tabIndex={-1}
          />
          <ListItemText primary={station.name} />
        </ListItemButton>
      ))}
    </List>
  );
};

const SchoolChecklist = ({
  companyId,
  selectedSchoolIds,
  onToggle,
  disabled,
}: {
  companyId: number;
  selectedSchoolIds: number[];
  onToggle: (schoolId: number) => void;
  disabled: boolean;
}) => {
  const {data, isLoading} = useRqListCmpySchoolsFacilities(companyId, true);

  const schools: ICompanySchool[] = data || [];

  if (isLoading) {
    return (
      <Box>
        <Skeleton height={36} />
        <Skeleton height={36} />
        <Skeleton height={36} />
      </Box>
    );
  }

  if (!schools.length) {
    return (
      <Typography variant='body2' color='text.secondary'>
        No schools found for this company.
      </Typography>
    );
  }

  return (
    <List
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        maxHeight: 240,
        overflow: 'auto',
      }}>
      {schools.map(school => (
        <ListItemButton
          key={school.id}
          onClick={() => onToggle(school.id)}
          disabled={disabled}
          divider>
          <Checkbox
            checked={selectedSchoolIds.includes(school.id)}
            disableRipple
            tabIndex={-1}
          />
          <ListItemText primary={school.name} />
        </ListItemButton>
      ))}
    </List>
  );
};
